import { registerIndicator, registerOverlay } from "klinecharts";
import { generatePivots, PivotPatternType } from "../utils/pivotDetector";
export function createPivotsEngine({ state, chart_colors, getEl }) {
    function ensurePivotDotOverlayRegistered() {
        if (state.pivotDotRegistered) return;
        try {
            registerOverlay({
                name: "pivotDot",
                createPointFigures: ({ coordinates, overlay }) => {
                    const point = coordinates?.[0];
                    if (!point) return [];
                    const isHigh = overlay.extendData?.isHigh;
                    const color = isHigh ? chart_colors.pivotHigh : chart_colors.pivotLow;
                    const r = overlay.styles?.point?.radius ?? 3;
                    return [
                        {
                            type: "circle",
                            attrs: { x: point.x, y: point.y, r },
                            styles: { color, fillColor: color },
                        },
                    ];
                },
            });
            state.pivotDotRegistered = true;
        } catch (e) {
            state.pivotDotRegistered = false;
        }
    }
    function clearPivotOverlays() {
        if (!state.pivotOverlayIds?.length) return;
        if (typeof state.chart?.removeOverlay === "function") {
            state.pivotOverlayIds.forEach((id) => state.chart.removeOverlay(id));
        } else if (typeof state.chart?.removeOverlays === "function") {
            state.chart.removeOverlays(state.pivotOverlayIds);
        }
        state.pivotOverlayIds = [];
    }
    function getDynamicPivotMarkLimit(pivotsLength) {
        const getVisibleCount = () => {
            try {
                if (typeof state.chart?.getVisibleRange === "function") {
                    const r = state.chart.getVisibleRange();
                    const from = Number.isFinite(r?.from) ? r.from : r?.start;
                    const to = Number.isFinite(r?.to) ? r.to : r?.end;
                    if (Number.isFinite(from) && Number.isFinite(to)) return Math.max(0, to - from + 1);
                }
            } catch (_) { }
            return null;
        };
        let visible = getVisibleCount();
        if (!Number.isFinite(visible) || visible <= 0) {
            const el = getEl?.();
            const containerWidth = el?.clientWidth;
            let barSpacing = null;
            try {
                barSpacing =
                    state.chart?.getStyles?.()?.candle?.barSpacing ??
                    state.chart?.getStyles?.()?.candle?.bar?.spacing ??
                    state.chart?.getStyles?.()?.candle?.bar?.barSpacing ??
                    state.chart?.getOptions?.()?.candle?.barSpacing ??
                    null;
            } catch (_) { }

            if (Number.isFinite(containerWidth) && Number.isFinite(barSpacing) && barSpacing > 0) {
                visible = Math.floor(containerWidth / barSpacing);
            }
        }
        if (!Number.isFinite(visible) || visible <= 0) {
            const totalBars = state.bars?.length ?? pivotsLength;
            const fraction =
                pivotsLength > 0
                    ? Math.min(1, Math.max(0, totalBars / (totalBars + pivotsLength)))
                    : 1;
            const proportional = Math.ceil(pivotsLength * fraction);
            return Math.min(pivotsLength, Math.max(1, proportional));
        }
        const limit = Math.max(1, Math.round(visible * 2));
        return Math.min(limit, pivotsLength);
    }
    function markPivotsOnChart() {
        if (!state.chart || !state.bars?.length) return;
        const { pivots } = generatePivots(state.bars);
        clearPivotOverlays();
        const byTs = new Map(state.bars.map((b) => [b.timestamp, b]));
        const maxMarks = getDynamicPivotMarkLimit(pivots.length);
        const shown = pivots.length > maxMarks ? pivots.slice(-maxMarks) : pivots;
        for (const p of shown) {
            const candle = byTs.get(p.timestamp);
            if (!candle) continue;
            const isHigh = p.type === PivotPatternType.HIGH;
            const range = Math.max(1e-6, candle.high - candle.low);
            const gap = range * 0.18;
            const value = isHigh
                ? Math.max(candle.open, candle.close) + gap
                : Math.min(candle.open, candle.close) - gap;
            if (state.pivotDotRegistered && typeof state.chart.createOverlay === "function") {
                const id = state.chart.createOverlay({
                    name: "pivotDot",
                    paneId: state.mainPaneId,
                    lock: true,
                    points: [{ timestamp: p.timestamp, value }],
                    extendData: { isHigh },
                    styles: { point: { radius: 3 } },
                    zLevel: 100,
                });
                if (id != null) state.pivotOverlayIds.push(id);
            }
        }
    }
    function pivotIndicatorRegistered(pivots) {
        state._pivotByTs = new Map(pivots.map((p) => [Number(p.timestamp), p]));
        if (state._pivotIndicatorRegistered) return;
        try {
            registerIndicator({
                name: "PIVOT_MARKS",
                shortName: "",
                calc: (kLineDataList) => {
                    const result = {};
                    for (const k of kLineDataList) {
                        const p = state._pivotByTs.get(Number(k.timestamp));
                        if (!p) continue;
                        result[k.timestamp] = {
                            price: p.price,
                            type: p.type,
                            anchor: p.anchor,
                            bar1: p.bar1,
                            bar2: p.bar2,
                        };
                    }
                    return result;
                },
                createTooltipDataSource: ({ crosshair, indicator }) => {
                    const ts = crosshair?.kLineData?.timestamp;
                    if (ts == null) return { legends: [] };
                    const r = indicator?.result?.[ts];
                    if (!r) return { legends: [] };
                    const isHigh = r.type === PivotPatternType.HIGH;
                    const fmtPrice = (x) => Number(x).toLocaleString("en-IN");
                    const fmtDate = (t) =>
                        new Date(Number(t)).toLocaleString("en-IN", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    const line = (label, c) => {
                        if (!c) return null;
                        return {
                            title: `${label}: `,
                            value: `${fmtDate(c.timestamp)} | O:${fmtPrice(c.open)} H:${fmtPrice(c.high)} L:${fmtPrice(c.low)} C:${fmtPrice(c.close)}`,
                        };
                    };
                    return {
                        legends: [
                            { title: `PIVOT (${isHigh ? "H" : "L"}): `, value: fmtPrice(r.price) },
                            { title: "Pivot Date: ", value: fmtDate(ts) },
                            line("Anchor", r.anchor),
                            line("Bar1", r.bar1),
                            line("Bar2", r.bar2),
                        ].filter(Boolean),
                    };
                },
            });
            state._pivotIndicatorRegistered = true;
        } catch {
            state._pivotIndicatorRegistered = true;
        }
    }
    function ensurePivotTooltipIndicator() {
        if (!state.chart || !state.bars?.length) return;
        const { pivots } = generatePivots(state.bars);
        pivotIndicatorRegistered(pivots);
        try {
            state.chart.removeIndicator?.("PIVOT_MARKS", true);
        } catch (_) { }
        try {
            state.chart.createIndicator?.({ name: "PIVOT_MARKS" }, true, { id: state.mainPaneId });
            state.pivotIndicatorAdded = true;
        } catch (_) { }
    }
    function clearPivotMarksOnly() {
        if (!state.pivotMarkIds) state.pivotMarkIds = [];
        for (const id of state.pivotMarkIds) {
            state.chart?.removeAnnotation?.(id);
            state.chart?.removeGraphicMark?.(id);
        }
        state.pivotMarkIds = [];
    }
    return {
        ensurePivotDotOverlayRegistered,
        ensurePivotTooltipIndicator,
        markPivotsOnChart,
        clearPivotOverlays,
        clearPivotMarksOnly,
    };
}
